import React, { useState, useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import BlockIcon from '@mui/icons-material/Block';
import CircularProgress from '@mui/material/CircularProgress';
import { supabase } from '../utils/supabaseClient';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PaidIcon from '@mui/icons-material/Paid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LeaderboardsView from '../components/LeaderboardsView';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Skeleton from '@mui/material/Skeleton';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Helper function for consistent date handling
const getAdjustedDate = (dateInput) => {
  // Handle if input is already a Date object
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setUTCHours(12, 0, 0, 0);
    return date;
  }
  
  // Handle string input
  if (typeof dateInput === 'string') {
    // If it's already an ISO string with time, use it as is
    if (dateInput.includes('T')) {
      return new Date(dateInput);
    }
    // Otherwise append noon UTC time to ensure date consistency
    return new Date(`${dateInput}T12:00:00Z`);
  }
  
  // Return null for undefined or null input
  return null;
};

// Create skeleton components for loading states
const MetricCardSkeleton = ({ height }) => (
  <Card sx={{ height: height || '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
        <Skeleton variant="text" width={160} />
      </Box>
      <Skeleton variant="text" width="70%" height={42} />
      <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
    </CardContent>
  </Card>
);

const ChartSkeleton = () => (
  <Box sx={{ pt: 2, height: 400, position: 'relative' }}>
    {/* X-axis */}
    <Skeleton variant="rectangular" height={2} width="100%" sx={{ position: 'absolute', bottom: 40, bgcolor: 'rgba(0,0,0,0.12)' }} />
    
    {/* Y-axis */}
    <Skeleton variant="rectangular" height="calc(100% - 40px)" width={2} sx={{ position: 'absolute', left: 40, top: 0, bgcolor: 'rgba(0,0,0,0.12)' }} />
    
    {/* Legend placeholders */}
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="rectangular" width={16} height={16} sx={{ mr: 1, bgcolor: 'rgba(54, 162, 235, 0.6)' }} />
        <Skeleton variant="text" width={80} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="rectangular" width={16} height={16} sx={{ mr: 1, bgcolor: 'rgba(75, 192, 192, 0.6)' }} />
        <Skeleton variant="text" width={100} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="rectangular" width={16} height={16} sx={{ mr: 1, bgcolor: 'rgba(255, 99, 132, 0.6)' }} />
        <Skeleton variant="text" width={90} />
      </Box>
    </Box>
    
    {/* Fake state labels on x-axis */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'absolute', bottom: 0, left: 40, right: 20, px: 2 }}>
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} variant="text" width={30} />
      ))}
    </Box>
    
    {/* Fake bars */}
    <Box sx={{ display: 'flex', justifyContent: 'space-around', height: 'calc(100% - 80px)', mt: 2, pt: 2 }}>
      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%', maxWidth: 80 }}>
          <Skeleton 
            variant="rectangular" 
            width={20} 
            height={`${Math.random() * 60 + 20}%`} 
            sx={{ mb: 0.5, bgcolor: 'rgba(54, 162, 235, 0.6)' }} 
          />
          <Skeleton 
            variant="rectangular" 
            width={20} 
            height={`${Math.random() * 40 + 10}%`} 
            sx={{ mb: 0.5, bgcolor: 'rgba(75, 192, 192, 0.6)' }} 
          />
          <Skeleton 
            variant="rectangular" 
            width={20} 
            height={`${Math.random() * 20 + 5}%`} 
            sx={{ bgcolor: 'rgba(255, 99, 132, 0.6)' }} 
          />
        </Box>
      ))}
    </Box>
  </Box>
);

const TableSkeleton = () => (
  <Box sx={{ pt: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
    </Box>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><Skeleton variant="text" width={60} /></TableCell>
            <TableCell align="right"><Skeleton variant="text" width={100} /></TableCell>
            <TableCell align="right"><Skeleton variant="text" width={130} /></TableCell>
            <TableCell align="right"><Skeleton variant="text" width={120} /></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(8)].map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton variant="text" width={30} /></TableCell>
              <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
              <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
              <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const RecentUploadsSkeleton = () => (
  <Grid container spacing={3}>
    {[1, 2, 3].map((item) => (
      <Grid item xs={12} md={4} key={item}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="70%" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="65%" />
          </CardContent>
          <CardActions>
            <Skeleton variant="rectangular" width={100} height={30} />
          </CardActions>
        </Card>
      </Grid>
    ))}
  </Grid>
);

function Dashboard() {
  const navigate = useNavigate();
  const [recentUploads, setRecentUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'
  const [metrics, setMetrics] = useState({
    totalCokeRevenue: 0,
    totalGrossProfit: 0,
    totalProviderPayment: 0,
    totalLocations: 0,
    totalUnits: 0,
    completedLocations: 0,
    completedUnits: 0,
    refusalLocations: 0,
    refusalUnits: 0
  });
  const [revenueByState, setRevenueByState] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(null); // null means "All Time"
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  // Update ref declarations
  const prevProviderRef = useRef('');
  const prevWeekRef = useRef(null);
  const isMountedRef = useRef(true);
  // Change fetchInProgressRef to store the ID of the fetch in progress
  const fetchInProgressIdRef = useRef(0);
  const fetchDebounceTimerRef = useRef(null);
  const fetchIdRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const lastSuccessfulFetchIdRef = useRef(0);

  useEffect(() => {
    const isFirstMount = isInitialMountRef.current;
    console.log(`Dashboard component mounted (Initial: ${isFirstMount})`);
    isMountedRef.current = true;
    // Mark initial mount check as done *after* the first run
    isInitialMountRef.current = false;

    // Only fetch if no other fetch is currently running
    if (fetchInProgressIdRef.current === 0) {
      const currentFetchId = ++fetchIdRef.current;
      console.log(`Initiating fetch (FetchID: ${currentFetchId})`);
      fetchRecentUploads();
      // Pass the specific fetchId to the function
      fetchDashboardMetrics(currentFetchId);
    } else {
      console.log(`Skipping fetch on mount/remount as fetch (FetchID: ${fetchInProgressIdRef.current}) is already in progress.`);
    }

    return () => {
      console.log('Dashboard component unmounted');
      isMountedRef.current = false;
      if (fetchDebounceTimerRef.current) {
        clearTimeout(fetchDebounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Skip if this is the first mount
    if (isInitialMountRef.current) {
      return;
    }

    const providerChanged = selectedProvider !== prevProviderRef.current;
    const weekChanged = (selectedWeek === null && prevWeekRef.current !== null) ||
                       (selectedWeek !== null && prevWeekRef.current === null) ||
                       (selectedWeek !== null && prevWeekRef.current !== null &&
                        selectedWeek.getTime() !== prevWeekRef.current.getTime());

    if (providerChanged || weekChanged) {
      // Increment fetchIdRef for the *new intention* to fetch
      const currentFetchId = ++fetchIdRef.current;
      console.log(`Filter changed - Provider: ${selectedProvider}, Week: ${selectedWeek ? selectedWeek.toISOString() : 'null'}, Intending FetchID: ${currentFetchId}`);

      // Update previous refs to reflect the state *triggering* this potential fetch
      prevProviderRef.current = selectedProvider;
      prevWeekRef.current = selectedWeek;

      if (fetchDebounceTimerRef.current) {
        clearTimeout(fetchDebounceTimerRef.current);
      }

      fetchDebounceTimerRef.current = setTimeout(() => {
        // Check if component is still mounted AND if this is still the most recent intended fetch
        if (isMountedRef.current && currentFetchId === fetchIdRef.current) {
          // Check if another fetch is already running
          if (fetchInProgressIdRef.current === 0) {
            console.log(`Debounced fetch starting (FetchID: ${currentFetchId})`);
            fetchDashboardMetrics(currentFetchId);
          } else {
            console.log(`Skipped debounced fetch (FetchID: ${currentFetchId}) - Fetch (FetchID: ${fetchInProgressIdRef.current}) already in progress.`);
          }
        } else {
          console.log(`Skipped debounced fetch (FetchID: ${currentFetchId}, Latest Intended: ${fetchIdRef.current}, Mounted: ${isMountedRef.current})`);
        }
      }, 300); // 300ms debounce
    }
  }, [selectedProvider, selectedWeek]);

  async function fetchRecentUploads() {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('uploads')
        .select('*')
        .eq('is_archived', false)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) {
        console.error('Error fetching recent uploads:', error);
        return;
      }
      
      setRecentUploads(data || []);
    } catch (error) {
      console.error('Error in fetching recent uploads:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDashboardMetrics(fetchId) {
    // Check if a fetch is already marked as in progress
    if (fetchInProgressIdRef.current !== 0) {
      console.log(`Fetch (FetchID: ${fetchId}) initiated, but fetch (FetchID: ${fetchInProgressIdRef.current}) is already in progress. Skipping.`);
      return;
    }

    console.log(`Starting dashboard metrics fetch (FetchID: ${fetchId})`);

    try {
      // Mark this fetch as in progress *using its specific ID*
      fetchInProgressIdRef.current = fetchId;
      setMetricsLoading(true);

      // Get auth token
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;

      if (!token) {
        console.error('No authentication token available');
        return;
      }
      
      // Prepare URL with optional provider filter and work week filter
      const params = new URLSearchParams();
      
      if (selectedProvider) {
        params.append('provider', selectedProvider);
      }
      
      if (selectedWeek) {
        const weekStartDate = startOfWeek(selectedWeek, { weekStartsOn: 1 }); // Monday
        const weekEndDate = endOfWeek(selectedWeek, { weekStartsOn: 1 }); // Sunday
        
        const adjustedFromDate = getAdjustedDate(weekStartDate);
        const adjustedToDate = getAdjustedDate(weekEndDate);
        
        params.append('work_week_from_date', adjustedFromDate.toISOString());
        params.append('work_week_to_date', adjustedToDate.toISOString());
      }
      
      let url = '/api/dashboard/metrics';
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log(`Making API request to: ${url} (FetchID: ${fetchId})`);
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Check if component unmounted while fetching
      if (!isMountedRef.current) {
        console.log(`Component unmounted, discarding results (FetchID: ${fetchId})`);
        return;
      }

      // Check if a *newer* fetch has already *successfully completed*
      if (fetchId < lastSuccessfulFetchIdRef.current) {
        console.log(`Stale fetch results (FetchID: ${fetchId}), newer results (FetchID: ${lastSuccessfulFetchIdRef.current}) already applied. Discarding.`);
        return;
      }

      console.log(`Received API response, applying data (FetchID: ${fetchId})`);

      if (response.data) {
        // Log received data to debug
        console.log('Revenue By State data:', response.data.revenueByState);
        
        setMetrics(response.data.metrics || {
          totalCokeRevenue: 0,
          totalGrossProfit: 0,
          totalProviderPayment: 0,
          totalLocations: 0,
          totalUnits: 0,
          completedLocations: 0,
          completedUnits: 0,
          refusalLocations: 0,
          refusalUnits: 0
        });
        setRevenueByState(response.data.revenueByState || []);
        setProviders(response.data.providers || []);
        
        // Update last successful fetch ID *after* setting state successfully
        lastSuccessfulFetchIdRef.current = fetchId;
        console.log(`Updated dashboard metrics state (FetchID: ${fetchId}, LastSuccessful: ${lastSuccessfulFetchIdRef.current})`);
      } else {
        console.log(`Received no data in response (FetchID: ${fetchId})`);
      }
    } catch (error) {
      console.error(`Error fetching dashboard metrics (FetchID: ${fetchId}):`, error);
      // Only reset state if the component is still mounted and this wasn't a stale fetch
      if (isMountedRef.current && fetchId >= lastSuccessfulFetchIdRef.current) {
        setMetrics({
          totalCokeRevenue: 0,
          totalGrossProfit: 0,
          totalProviderPayment: 0,
          totalLocations: 0,
          totalUnits: 0,
          completedLocations: 0,
          completedUnits: 0,
          refusalLocations: 0,
          refusalUnits: 0
        });
        setRevenueByState([]);
        setProviders([]);
      }
    } finally {
      // Only reset the 'in progress' flag if this *completing* fetch 
      // is the one currently marked as in progress
      if (fetchInProgressIdRef.current === fetchId) {
        fetchInProgressIdRef.current = 0; // Mark fetch as completed
      }
      
      // Only turn off loading indicator if no *other* fetch has started
      // in the meantime (i.e., if fetchInProgressIdRef is now 0)
      if (fetchInProgressIdRef.current === 0) {
        // Check mount status again before setting state in finally
        if (isMountedRef.current) {
          setMetricsLoading(false);
        }
      }
      console.log(`Completed dashboard metrics fetch processing (FetchID: ${fetchId}, InProgressID: ${fetchInProgressIdRef.current})`);
    }
  }

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd');
    } catch (error) {
      return dateString;
    }
  };
  
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (number) => {
    if (!number && number !== 0) return '0';
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0%';
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  const handleProviderChange = (event) => {
    setSelectedProvider(event.target.value);
  };

  const handleWeekChange = (newDate) => {
    setSelectedWeek(newDate);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Prepare chart data
  const sortedRevenueData = [...revenueByState].map(item => ({
    ...item,
    completionRevenue: item.completionRevenue || 0,
    refusalRevenue: item.refusalRevenue || 0
  })).sort((a, b) => b.revenue - a.revenue);
  
  // Log chart data for debugging
  console.log('Revenue breakdown for chart:');
  sortedRevenueData.forEach(item => {
    console.log(`State: ${item.state}, Total: ${item.revenue}, Completion: ${item.completionRevenue}, Refusal: ${item.refusalRevenue}`);
  });
  
  const chartData = {
    labels: sortedRevenueData.map(item => item.state),
    datasets: [
      {
        label: 'Total Revenue',
        data: sortedRevenueData.map(item => item.revenue || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
      {
        label: 'Completion Revenue',
        data: sortedRevenueData.map(item => item.completionRevenue || 0),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Refusal Revenue',
        data: sortedRevenueData.map(item => item.refusalRevenue || 0),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: false, // Don't stack the bars horizontally
      },
      y: {
        stacked: false, // Don't stack the bars vertically
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const handleExportCSV = () => {
    // Prepare CSV data
    const csvContent = [
      ['State', 'Total Revenue', 'Completion Revenue', 'Refusal Revenue'],
      ...sortedRevenueData.map(item => [
        item.state,
        // Remove $ and commas, but keep decimal places
        (item.revenue || 0).toFixed(2),
        (item.completionRevenue || 0).toFixed(2),
        (item.refusalRevenue || 0).toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `revenue_by_state_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Revenue Table Component
  const RevenueTable = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          size="small"
          variant="outlined"
        >
          Export CSV
        </Button>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>State</TableCell>
              <TableCell align="right">Total Revenue</TableCell>
              <TableCell align="right">Completion Revenue</TableCell>
              <TableCell align="right">Refusal Revenue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRevenueData.map((item) => (
              <TableRow key={item.state}>
                <TableCell component="th" scope="row">{item.state}</TableCell>
                <TableCell align="right">{formatCurrency(item.revenue || 0)}</TableCell>
                <TableCell align="right">{formatCurrency(item.completionRevenue || 0)}</TableCell>
                <TableCell align="right">{formatCurrency(item.refusalRevenue || 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mt: 3 }}>
        {/* Tab Navigation */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            aria-label="dashboard tabs"
          >
            <Tab 
              icon={<DashboardIcon />} 
              label="Dashboard" 
              id="dashboard-tab"
              aria-controls="dashboard-panel"
            />
            <Tab 
              icon={<LeaderboardIcon />} 
              label="Leaderboards" 
              id="leaderboards-tab"
              aria-controls="leaderboards-panel"
            />
          </Tabs>
        </Paper>

        {activeTab === 0 ? (
          <>
            {/* Top Metrics Cards */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5">
                  Dashboard Metrics
                  <Box component="span" sx={{ ml: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    {selectedProvider && (
                      <Chip 
                        label={`${selectedProvider}`} 
                        color="secondary" 
                        size="small"
                        onDelete={() => setSelectedProvider('')}
                      />
                    )}
                    {selectedWeek && (
                      <Chip 
                        label={`Week of ${format(startOfWeek(selectedWeek, { weekStartsOn: 1 }), 'MMM d')}`} 
                        color="primary" 
                        size="small"
                        onDelete={() => setSelectedWeek(null)}
                      />
                    )}
                  </Box>
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                    <InputLabel id="provider-filter-label">Filter by Provider</InputLabel>
                    <Select
                      labelId="provider-filter-label"
                      id="provider-filter"
                      value={selectedProvider}
                      onChange={handleProviderChange}
                      label="Filter by Provider"
                      size="small"
                    >
                      <MenuItem value="">
                        <em>All Providers</em>
                      </MenuItem>
                      {(providers || []).map((provider) => (
                        <MenuItem key={provider} value={provider}>
                          {provider}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <DatePicker
                    label="Filter by Work Week"
                    value={selectedWeek}
                    onChange={handleWeekChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { minWidth: 200 },
                        helperText: selectedWeek 
                          ? `Week of ${format(startOfWeek(selectedWeek, { weekStartsOn: 1 }), 'MMM d')} - ${format(endOfWeek(selectedWeek, { weekStartsOn: 1 }), 'MMM d, yyyy')}`
                          : 'All Time (no filter)'
                      },
                      actionBar: {
                        actions: ['clear']
                      }
                    }}
                  />
                </Box>
              </Box>

              {metricsLoading ? (
                <Grid container spacing={3}>
                  {/* Primary metrics skeleton cards */}
                  {[1, 2, 3].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={`primary-${item}`}>
                      <MetricCardSkeleton />
                    </Grid>
                  ))}
                  
                  {/* Secondary metrics skeleton cards */}
                  {[1, 2].map((item) => (
                    <Grid item xs={12} sm={6} md={6} key={`secondary-${item}`}>
                      <MetricCardSkeleton />
                    </Grid>
                  ))}
                  
                  {/* Completed work section header skeleton */}
                  <Grid item xs={12}>
                    <Skeleton variant="text" width={200} height={32} />
                  </Grid>
                  
                  {/* Completed work skeleton cards */}
                  {[1, 2].map((item) => (
                    <Grid item xs={12} sm={6} key={`completed-${item}`}>
                      <MetricCardSkeleton height="100%" />
                    </Grid>
                  ))}
                  
                  {/* Refusal work section header skeleton */}
                  <Grid item xs={12}>
                    <Skeleton variant="text" width={200} height={32} />
                  </Grid>
                  
                  {/* Refusal work skeleton cards */}
                  {[1, 2].map((item) => (
                    <Grid item xs={12} sm={6} key={`refusal-${item}`}>
                      <MetricCardSkeleton height="100%" />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="h6">Total Coke Revenue</Typography>
                        </Box>
                        <Typography variant="h4" color="primary">
                          {formatCurrency(metrics.totalCokeRevenue)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AccountBalanceWalletIcon color="success" sx={{ mr: 1 }} />
                          <Typography variant="h6">Total Gross Profit</Typography>
                        </Box>
                        <Typography variant="h4" color="success.main">
                          {formatCurrency(metrics.totalGrossProfit)}
                        </Typography>
                        <Typography variant="body1" color="success.main" sx={{ mt: 1 }}>
                          Margin: {formatPercentage(metrics.totalGrossProfit / metrics.totalCokeRevenue)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PaidIcon color="secondary" sx={{ mr: 1 }} />
                          <Typography variant="h6">Total Provider Payments</Typography>
                        </Box>
                        <Typography variant="h4" color="secondary.main">
                          {formatCurrency(metrics.totalProviderPayment)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOnIcon color="info" sx={{ mr: 1 }} />
                          <Typography variant="h6">Total Locations</Typography>
                        </Box>
                        <Typography variant="h4" color="info.main">
                          {formatNumber(metrics.totalLocations)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ApartmentIcon color="warning" sx={{ mr: 1 }} />
                          <Typography variant="h6">Total Units</Typography>
                        </Box>
                        <Typography variant="h4" color="warning.main">
                          {formatNumber(metrics.totalUnits)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* New cards for completed locations and units */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Completed Work Breakdown
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ height: '100%', bgcolor: 'success.light' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOnIcon sx={{ mr: 1, color: 'white' }} />
                          <Typography variant="h6" sx={{ color: 'white' }}>Completed Locations</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: 'white' }}>
                          {formatNumber(metrics.completedLocations)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ height: '100%', bgcolor: 'success.light' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ApartmentIcon sx={{ mr: 1, color: 'white' }} />
                          <Typography variant="h6" sx={{ color: 'white' }}>Completed Units</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: 'white' }}>
                          {formatNumber(metrics.completedUnits)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* New cards for refusal locations and units */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Refusal Work Breakdown
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ height: '100%', bgcolor: 'error.light' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOnIcon sx={{ mr: 1, color: 'white' }} />
                          <Typography variant="h6" sx={{ color: 'white' }}>Refusal Locations</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: 'white' }}>
                          {formatNumber(metrics.refusalLocations)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ height: '100%', bgcolor: 'error.light' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ApartmentIcon sx={{ mr: 1, color: 'white' }} />
                          <Typography variant="h6" sx={{ color: 'white' }}>Refusal Units</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ color: 'white' }}>
                          {formatNumber(metrics.refusalUnits)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Paper>

            {/* Revenue by State Chart */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  Revenue by State with Completion/Refusal Breakdown
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="view mode"
                  size="small"
                >
                  <ToggleButton value="chart" aria-label="chart view">
                    <BarChartIcon />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="table view">
                    <TableChartIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              {metricsLoading ? (
                viewMode === 'chart' ? <ChartSkeleton /> : <TableSkeleton />
              ) : revenueByState.length > 0 ? (
                viewMode === 'chart' ? (
                  <Box sx={{ height: 400, pt: 2 }}>
                    <Bar data={chartData} options={chartOptions} />
                  </Box>
                ) : (
                  <Box sx={{ pt: 2 }}>
                    <RevenueTable />
                  </Box>
                )
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No revenue data available.
                  </Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                Weekly Contractor Payment Calculator
              </Typography>
              <Typography paragraph>
                Automate the calculation of weekly contractor payments based on completed work data and the provider rate table.
              </Typography>

              <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                Upload Options
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Button 
                    fullWidth
                    variant="contained" 
                    size="large"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => navigate('/upload-data')}
                    sx={{ py: 2 }}
                  >
                    Upload Completed Work
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button 
                    fullWidth
                    variant="contained" 
                    color="secondary"
                    size="large"
                    startIcon={<BlockIcon />}
                    onClick={() => navigate('/upload-refusal')}
                    sx={{ py: 2 }}
                  >
                    Upload Refusal Work
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" sx={{ mb: 2 }}>
                Management Options
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Button 
                    fullWidth
                    variant="outlined" 
                    size="large"
                    startIcon={<TableChartIcon />}
                    onClick={() => navigate('/rate-table')}
                    sx={{ py: 2 }}
                  >
                    Manage Rate Table
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Recent Uploads
              </Typography>
              
              {loading ? (
                <RecentUploadsSkeleton />
              ) : recentUploads.length > 0 ? (
                <Grid container spacing={3}>
                  {recentUploads.map((upload) => (
                    <Grid item xs={12} md={4} key={upload.id}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            {formatDate(upload.created_at)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {upload.filename}
                          </Typography>
                          <Typography variant="body1">
                            Status: {upload.status}
                          </Typography>
                          {upload.file_path && (
                            <Typography variant="body2" color="text.secondary">
                              Type: {upload.file_path.includes('/refusal/') ? 'Refusal Work' : 'Completed Work'}
                            </Typography>
                          )}
                          {upload.uploaded_by && (
                            <Typography variant="body2" color="text.secondary">
                              Uploaded By: {upload.uploaded_by}
                            </Typography>
                          )}
                          {upload.total_amount && (
                            <Typography variant="body1">
                              Total Amount: {formatCurrency(upload.total_amount)}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          {upload.status === 'completed' || upload.status === 'processed' ? (
                            <Button 
                              size="small" 
                              color="primary" 
                              onClick={() => navigate(`/results/${upload.id}?refusal=${upload.file_path.includes('/refusal/')}`)}
                            >
                              View Results
                            </Button>
                          ) : (
                            <Button 
                              size="small" 
                              color="secondary" 
                              onClick={() => navigate(`/calculate/${upload.id}`)}
                            >
                              Calculate
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No uploads found. Start by uploading a data file.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/upload-data')}
                  >
                    Upload Data
                  </Button>
                </Box>
              )}
            </Paper>
          </>
        ) : (
          <LeaderboardsView 
            selectedProvider={selectedProvider}
            selectedWeek={selectedWeek}
            formatCurrency={formatCurrency}
            formatNumber={formatNumber}
            formatPercentage={formatPercentage}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default Dashboard; 